import React from "react";
import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import { BloomsTaxonomyActivitiesResponseDto } from "@packages/api/madlenAPI.schemas";
import { useTranslation } from "react-i18next";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { PdfLayout } from "../pdf/PdfLayout";
import { commonStyles } from "../pdf/CommonPdfStyles";
import MarkdownRenderer from "../../../tools/components/custom-markdown";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";

interface BloomTaxonomyCardPDFProps {
  bloomsTaxonomyActivity: BloomsTaxonomyActivitiesResponseDto;
  customizationSettings: PdfCustomizationSettings | undefined;
}

// Register font
Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

const styles = StyleSheet.create({
  activityContainer: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#FFF7E6",
    borderRadius: 8,
  },
  activityTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#D48806",
    marginBottom: 12,
  },
  activityLevel: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#D48806",
    marginBottom: 8,
  },
  descriptionContainer: {
    marginBottom: 15,
  },
  twoColumnLayout: {
    flexDirection: "row",
    gap: 12,
  },
  column: {
    flex: 1,
  },
  contentTitle: {
    color: "#81C784",
  },
  instructionsTitle: {
    color: "#66BB6A",
  },
  assessmentTitle: {
    color: "#D48806",
  },
  deliverableTitle: {
    color: "#F3C969",
  },
  implementationContainer: {
    marginTop: 15,
    padding: 12,
    backgroundColor: "#E6F4EA",
    borderRadius: 8,
  },
  ethicalTipsContainer: {
    marginTop: 15,
    padding: 12,
    backgroundColor: "#E1F5FE", // Light blue background
    borderRadius: 8,
  },
  listItemContainer: {
    flexDirection: "row",
    marginBottom: 6,
  },
  bullet: {
    width: 10,
    fontSize: 10,
  },
  listItemContent: {
    flex: 1,
    fontSize: 10,
  },
  sourceText: {
    fontSize: 8,
    color: "#666666",
    fontStyle: "italic",
    marginTop: 4,
    textAlign: "center",
  },
});

const BloomTaxonomyCardDocument = ({
  bloomsTaxonomyActivity,
  customizationSettings,
}: BloomTaxonomyCardPDFProps) => {
  const { t } = useTranslation();

  const markdownSectionContentStyle = {
    text: {
      text: {
        ...commonStyles.sectionContent,
        fontSize: scaleFontSize(
          commonStyles.sectionContent.fontSize,
          customizationSettings?.fontSize,
        ),
      },
    },
  };

  return (
    <PdfLayout
      courseName={bloomsTaxonomyActivity.courseName}
      gradeLevel={bloomsTaxonomyActivity.gradeLevel}
      topic={bloomsTaxonomyActivity.title}
      imageUrl={bloomsTaxonomyActivity.imageUrl}
      headerTextColor="#D48806"
      customizationSettings={customizationSettings}
      author={bloomsTaxonomyActivity.authorName}
      subject={bloomsTaxonomyActivity.courseName}
      createdDate={bloomsTaxonomyActivity.createdAt}
    >
      {/* Description */}
      {bloomsTaxonomyActivity.description && (
        <View style={styles.descriptionContainer}>
          <MarkdownRenderer
            customStyle={markdownSectionContentStyle}
            content={bloomsTaxonomyActivity.description}
          />

          {/* Citation */}
          <Text
            style={[
              styles.sourceText,
              {
                fontSize: scaleFontSize(
                  styles.sourceText.fontSize,
                  customizationSettings?.fontSize,
                ),
              },
            ]}
          >
            {t("bloomsTaxonomyActivity.disclaimer")}
          </Text>
        </View>
      )}

      {/* Ethical Usage Tips */}
      <View>
        <Text
          style={[
            commonStyles.sectionTitle,
            {
              color: "#3B82F6",
              marginTop: 15,
              marginBottom: 10,
              fontSize: scaleFontSize(
                commonStyles.sectionTitle.fontSize,
                customizationSettings?.fontSize,
              ),
            }, // Blue color for ethics
          ]}
        >
          {t("bloomsTaxonomyActivity.ethicalUsageTips")}
        </Text>
        <View style={styles.ethicalTipsContainer}>
          <View style={styles.listItemContainer}>
            <Text style={styles.bullet}>• </Text>
            <MarkdownRenderer
              customStyle={markdownSectionContentStyle}
              content={t("bloomsTaxonomyActivity.ethicTip1")}
            />
          </View>
          <View style={styles.listItemContainer}>
            <Text style={styles.bullet}>• </Text>
            <MarkdownRenderer
              customStyle={markdownSectionContentStyle}
              content={t("bloomsTaxonomyActivity.ethicTip2")}
            />
          </View>
          <View style={styles.listItemContainer}>
            <Text style={styles.bullet}>• </Text>
            <MarkdownRenderer
              customStyle={markdownSectionContentStyle}
              content={t("bloomsTaxonomyActivity.ethicTip3")}
            />
          </View>
          <View style={styles.listItemContainer}>
            <Text style={styles.bullet}>• </Text>
            <MarkdownRenderer
              customStyle={markdownSectionContentStyle}
              content={t("bloomsTaxonomyActivity.ethicTip4")}
            />
          </View>
        </View>
      </View>

      {/* Implementation Notes */}
      {bloomsTaxonomyActivity.implementationNotes &&
        bloomsTaxonomyActivity.implementationNotes.length > 0 && (
          <View wrap={false} style={{ marginBottom: 10 }}>
            <Text
              style={[
                commonStyles.sectionTitle,
                {
                  color: "#81C784",
                  marginTop: 15,
                  marginBottom: 10,
                  fontSize: scaleFontSize(
                    commonStyles.sectionTitle.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
              ]}
            >
              {t("bloomsTaxonomyActivity.implementationNotes")}
            </Text>
            <View style={styles.implementationContainer}>
              {bloomsTaxonomyActivity.implementationNotes.map((note, index) => (
                <View key={index} style={styles.listItemContainer}>
                  <Text style={styles.bullet}>• </Text>
                  <MarkdownRenderer
                    customStyle={markdownSectionContentStyle}
                    content={note}
                  />
                </View>
              ))}
            </View>
          </View>
        )}

      {/* Activities */}
      {bloomsTaxonomyActivity.activities &&
        bloomsTaxonomyActivity.activities.length > 0 && (
          <View>
            {bloomsTaxonomyActivity.activities.map((activity, index) => (
              <View key={index} style={styles.activityContainer} wrap={false}>
                {/* Activity Level */}
                {activity.level && (
                  <Text
                    style={[
                      styles.activityLevel,
                      {
                        fontSize: scaleFontSize(
                          styles.activityLevel.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                    ]}
                  >
                    {t("bloomsTaxonomyActivity.level")}:{" "}
                    {t(
                      "bloomsTaxonomyActivity." +
                        activity.level.toLowerCase() +
                        ".label",
                    )}
                  </Text>
                )}

                {/* Activity Title */}
                {activity.activity_title && (
                  <MarkdownRenderer
                    customStyle={{
                      text: {
                        text: {
                          ...styles.activityTitle,
                          fontSize: scaleFontSize(
                            styles.activityTitle.fontSize,
                            customizationSettings?.fontSize,
                          ),
                        },
                      },
                    }}
                    content={activity.activity_title}
                  />
                )}

                {/* Activity Description */}
                {activity.description && (
                  <MarkdownRenderer
                    customStyle={{
                      text: {
                        text: {
                          ...commonStyles.sectionContent,
                          marginBottom: 12,
                          fontSize: scaleFontSize(
                            commonStyles.sectionContent.fontSize,
                            customizationSettings?.fontSize,
                          ),
                        },
                      },
                    }}
                    content={activity.description}
                  />
                )}

                <View style={styles.twoColumnLayout}>
                  <View style={styles.column}>
                    {/* Instructions */}
                    {activity.instructions &&
                      activity.instructions.length > 0 && (
                        <View style={{ marginBottom: 12 }}>
                          <Text
                            style={[
                              commonStyles.sectionTitle,
                              styles.instructionsTitle,
                              {
                                fontSize: scaleFontSize(
                                  commonStyles.sectionTitle.fontSize,
                                  customizationSettings?.fontSize,
                                ),
                              },
                            ]}
                          >
                            {t("bloomsTaxonomyActivity.instructions")}
                          </Text>
                          {activity.instructions.map((instruction, idx) => (
                            <View key={idx} style={styles.listItemContainer}>
                              <Text style={styles.bullet}>• </Text>
                              <MarkdownRenderer
                                customStyle={markdownSectionContentStyle}
                                content={instruction}
                              />
                            </View>
                          ))}
                        </View>
                      )}
                  </View>

                  <View style={styles.column}>
                    {/* Deliverables */}
                    {activity.deliverables && (
                      <View style={{ marginBottom: 12 }}>
                        <Text
                          style={[
                            commonStyles.sectionTitle,
                            styles.deliverableTitle,
                            {
                              fontSize: scaleFontSize(
                                commonStyles.sectionTitle.fontSize,
                                customizationSettings?.fontSize,
                              ),
                            },
                          ]}
                        >
                          {t("bloomsTaxonomyActivity.deliverables")}
                        </Text>
                        <MarkdownRenderer
                          customStyle={markdownSectionContentStyle}
                          content={activity.deliverables}
                        />
                      </View>
                    )}

                    {/* Assessment Criteria */}
                    {activity.assessment_criteria &&
                      activity.assessment_criteria.length > 0 && (
                        <View style={{ marginBottom: 12 }}>
                          <Text
                            style={[
                              commonStyles.sectionTitle,
                              styles.assessmentTitle,
                              {
                                fontSize: scaleFontSize(
                                  commonStyles.sectionTitle.fontSize,
                                  customizationSettings?.fontSize,
                                ),
                              },
                            ]}
                          >
                            {t("bloomsTaxonomyActivity.assessmentCriteria")}
                          </Text>
                          {activity.assessment_criteria.map(
                            (criterion, idx) => (
                              <View key={idx} style={styles.listItemContainer}>
                                <Text style={styles.bullet}>• </Text>
                                <MarkdownRenderer
                                  customStyle={markdownSectionContentStyle}
                                  content={criterion}
                                />
                              </View>
                            ),
                          )}
                        </View>
                      )}

                    {/* AI Integration */}
                    {activity.ai_integration && (
                      <View>
                        <Text
                          style={[
                            commonStyles.sectionTitle,
                            styles.contentTitle,
                            {
                              fontSize: scaleFontSize(
                                commonStyles.sectionTitle.fontSize,
                                customizationSettings?.fontSize,
                              ),
                            },
                          ]}
                        >
                          {t("bloomsTaxonomyActivity.aiIntegration")}
                        </Text>
                        <MarkdownRenderer
                          customStyle={markdownSectionContentStyle}
                          content={activity.ai_integration}
                        />
                      </View>
                    )}
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}
    </PdfLayout>
  );
};

function BloomTaxonomyCardPDF(props: BloomTaxonomyCardPDFProps) {
  return <BloomTaxonomyCardDocument {...props} />;
}

export default BloomTaxonomyCardPDF;
